import { TranslationOption } from '@/constants/translate';
import { ESidebarTab } from '@/pages/Collaboration/constant';
import { getCollaborationSmartChapters } from '@/services/api_collaboration';
import { IChapterList, IKeyframeItem, IPG, ISpeakerItem } from '@/types/summary';
import { create } from 'zustand';

interface CollaborationState {
  sidebarActiveTab: ESidebarTab;
  showSidebar: boolean;
  chapterSummary: IChapterList[];
  srcContent: IPG[];
  meetingFinish: boolean;
  showExitDialog: boolean;
  showAiTooltip: boolean;
  showChapterDetail: boolean;
  unstableText: string;
  unstableTranslateText: string;
  avatarArr: string[];
  keyframeArr: IKeyframeItem[];
  recentSpeakerList: ISpeakerItem[];
  roleSpeakerMap: Record<string, string[]>; // role 到 speakerName 的映射
  focusedPara: IPG | null; // 当前聚焦的段落
  editedPiArr: string[]; // 已编辑过的段落ID列表
  waitingCorrectRecords: IPG[]; // 等待校正的记录列表
  translationOption: TranslationOption; // 翻译选项
}

interface ICollaborationStore extends CollaborationState {
  init: () => void;
  setSidebarActiveTab: (tab: ESidebarTab) => void;
  setShowSidebar: (show: boolean) => void;
  fetchChapterSummary: (streamId: string) => void;
  setChapterSummary: (chapterSummary: IChapterList[]) => void;
  setSrcContent: (srcContent: IPG[]) => void;
  setShowExitDialog: (show: boolean) => void;
  setShowAiTooltip: (show: boolean) => void;
  setChapterDetail: (show: boolean) => void;
  setUnstableText: (text: string) => void;
  setUnstableTransText: (text: string) => void;
  setMeetingFinish: (finish: boolean) => void;
  getAvatarIndex: (role: string) => number;
  setChapterKeyframe: (keyframeArr: IKeyframeItem[]) => void;
  getRecentSpeakers: () => ISpeakerItem[];
  unshiftRecentSpeakers: (newSpeakers: ISpeakerItem[]) => void;
  deleteRecentSpeakers: (speakers: string[]) => void;
  resetRecentSpeakers: () => void;
  setRoleSpeakerMapping: (role: string, speakerName: string[]) => void;
  getRoleSpeakerMapping: (role: string) => string[] | undefined;
  clearRoleSpeakerMapping: () => void;
  removeRoleSpeakerMapping: (role: string) => void;
  setFocusedPara: (focusedPara: IPG | null) => void;
  getFocusedPara: () => IPG | null;
  addEditedPiArr: (newEditedPi: string) => void;
  getEditedPiArr: () => string[];
  getWaitingCorrectRecords: () => IPG[];
  setWaitingCorrectRecords: (waitingCorrectRecords: IPG[]) => void;
  setTranslationOption: (option: TranslationOption) => void;
}

const CollaborationState: CollaborationState = {
  sidebarActiveTab: ESidebarTab.AIQA,
  showSidebar: false,
  chapterSummary: [],
  srcContent: [],
  meetingFinish: false,
  showExitDialog: false,
  showAiTooltip: false,
  showChapterDetail: false,
  unstableText: '',
  unstableTranslateText: '',
  avatarArr: [],
  keyframeArr: [],
  recentSpeakerList: [],
  roleSpeakerMap: {},
  focusedPara: null,
  editedPiArr: [],
  waitingCorrectRecords: [],
  translationOption: TranslationOption.both, // 默认值为原文+译文
};

const CollaborationStore = create<ICollaborationStore>()((set, get) => ({
  ...CollaborationState,
  init: () => {
    set(CollaborationState);
  },
  setSidebarActiveTab: (tab) => {
    set({ sidebarActiveTab: tab });
  },
  setShowSidebar: (show) => {
    set({ showSidebar: show });
  },
  setChapterSummary: (chapterSummary) => {
    set({ chapterSummary });
  },
  setChapterKeyframe: (keyframeArr) => {
    set({
      keyframeArr:keyframeArr.sort((a, b) => a.beginTimestamp - b.beginTimestamp),
    });
  },
  fetchChapterSummary: async (streamId) => {
    const res = await getCollaborationSmartChapters(streamId);
    res && set({ chapterSummary: res });
  },
  setSrcContent: (srcContent) => {
    set({ srcContent });
  },
  setShowExitDialog: (show) => {
    set({ showExitDialog: show });
  },
  setShowAiTooltip: (show) => {
    set({ showAiTooltip: show });
  },
  setChapterDetail: (show) => {
    set({ showChapterDetail: show });
  },
  setUnstableText: (text) => {
    set({ unstableText: text });
  },
  setUnstableTransText: (text) => {
    set({ unstableTranslateText: text });
  },
  setMeetingFinish: (finish) => {
    set({ meetingFinish: finish });
  },
  getAvatarIndex: (role) => {
    return Number(role)-1;
  },
  getRecentSpeakers: () => {
    const { recentSpeakerList } = get();
    return recentSpeakerList;
  },
  unshiftRecentSpeakers: (newSpeakers: ISpeakerItem[]) => {
    const { recentSpeakerList } = get();
    newSpeakers.forEach((newSpeaker) => {
      if (!newSpeaker.speakerName || !newSpeaker.speakerName.length) return;
      if (/^发言人\d*$/.test(newSpeaker.speakerName[0])) return;
      const recentSpeakerNameList = recentSpeakerList.map(
        (item) => item.speakerName[0]
      );
      // 先判断是否存在于当前列表中, 存在则移动到最前面
      if (recentSpeakerNameList.includes(newSpeaker.speakerName[0])) {
        const [element] = recentSpeakerList.splice(
          recentSpeakerNameList.indexOf(newSpeaker.speakerName[0]),
          1
        );
        recentSpeakerList.splice(0, 0, element);
      }
      // 否则直接插入到最前面
      else {
        recentSpeakerList.unshift(newSpeaker);
      }
    });
    set({ recentSpeakerList: [...recentSpeakerList] });
  },
  deleteRecentSpeakers: (speakers: string[]) => {
    const { recentSpeakerList } = get();
    const newRecentSpeakers = recentSpeakerList.filter(
      (speaker) => !speakers.includes(speaker.speakerName[0])
    );
    set({ recentSpeakerList: newRecentSpeakers });
  },
  resetRecentSpeakers: () => {
    set({ recentSpeakerList: [] });
  },
  setRoleSpeakerMapping: (role: string, speakerName: string[]) => {
    const { roleSpeakerMap } = get();
    set({
      roleSpeakerMap: {
        ...roleSpeakerMap,
        [role]: speakerName,
      },
    });
  },
  getRoleSpeakerMapping: (role: string) => {
    const { roleSpeakerMap } = get();
    return roleSpeakerMap[role];
  },
  clearRoleSpeakerMapping: () => {
    set({ roleSpeakerMap: {} });
  },
  removeRoleSpeakerMapping: (role: string) => {
    const { roleSpeakerMap } = get();
    const newMap = { ...roleSpeakerMap };
    delete newMap[role];
    set({ roleSpeakerMap: newMap });
  },
  setFocusedPara: (focusedPara) => {
    console.log("CollaborationStore setFocusedPara", focusedPara);
    set({ focusedPara });
  },
  getFocusedPara: () => {
    const { focusedPara } = get();
    return focusedPara;
  },
  addEditedPiArr: (newEditedPi: string) => {
    const { editedPiArr } = get();
    console.log("CollaborationStore addEditedPiArr", newEditedPi);
    set({ editedPiArr: [...editedPiArr, newEditedPi] });
  },
  getEditedPiArr: () => {
    const { editedPiArr } = get();
    return editedPiArr;
  },
  getWaitingCorrectRecords: () => {
    const { waitingCorrectRecords } = get();
    return waitingCorrectRecords;
  },
  setWaitingCorrectRecords: (waitingCorrectRecords) => {
    set({ waitingCorrectRecords });
  },
  setTranslationOption: (option) => {
    set({ translationOption: option });
  },
}));

export default CollaborationStore;
